/**
 * Multi-select campaigns in a searchable popover (one shared threshold value per row).
 *
 * Mirrors CountryMultiPicker with two necessary differences:
 *  - SEARCH-GATED. The country picker renders its whole list; there are ~100
 *    countries but thousands of campaigns, so an unfiltered list would paint a
 *    huge unvirtualized <ul>. Nothing is listed until 2 characters are typed,
 *    and the matches are capped — selections stay visible regardless.
 *  - Provider is shown per row: a Voluum "1" and a Binom "1" are different
 *    campaigns (and a Tres Commas id can equal a Voluum one), so the badge is
 *    what disambiguates them.
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { trackerFromEnum, type TagTrafficProvider } from '@trafficpulse/shared';

import { Checkbox, ProviderBadge, useModalPortalSurface } from '@/components/ui';
import { cn } from '@/lib/utils';

import { campaignKey, type CampaignOption } from './campaign-threshold-parts';

const TRIGGER_CLASS =
  'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';

const MIN_SEARCH_CHARS = 2;
const MAX_RENDERED_MATCHES = 50;

export interface CampaignSelection {
  provider: TagTrafficProvider;
  campaignId: string;
}

interface CampaignMultiPickerProps {
  options: CampaignOption[];
  selected: CampaignSelection[];
  onChange: (next: CampaignSelection[]) => void;
  maxKeys: number;
  isLoading?: boolean;
  disabled?: boolean;
}

export const CampaignMultiPicker: FC<CampaignMultiPickerProps> = ({
  options,
  selected,
  onChange,
  maxKeys,
  isLoading = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const portalSurface = useModalPortalSurface();

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const selectedSet = useMemo(
    () => new Set(selected.map((s) => campaignKey(s.provider, s.campaignId))),
    [selected],
  );

  const byKey = useMemo(() => {
    const m = new Map<string, CampaignOption>();
    for (const c of options) m.set(campaignKey(c.provider, c.id), c);
    return m;
  }, [options]);

  const query = search.trim().toLowerCase();
  const gated = query.length < MIN_SEARCH_CHARS;

  /** Already-picked campaigns stay listed so they can be unpicked without searching. */
  const visible = useMemo(() => {
    const chosen = options.filter((c) => selectedSet.has(campaignKey(c.provider, c.id)));
    if (gated) return chosen;
    const matches = options.filter((c) => {
      const k = campaignKey(c.provider, c.id);
      if (selectedSet.has(k)) return false;
      return c.name.toLowerCase().includes(query) || c.id.toLowerCase().includes(query);
    });
    return [...chosen, ...matches.slice(0, MAX_RENDERED_MATCHES)];
  }, [options, selectedSet, query, gated]);

  const totalMatches = useMemo(() => {
    if (gated) return 0;
    return options.filter(
      (c) => c.name.toLowerCase().includes(query) || c.id.toLowerCase().includes(query),
    ).length;
  }, [options, query, gated]);

  const toggle = useCallback(
    (option: CampaignOption) => {
      const k = campaignKey(option.provider, option.id);
      if (selectedSet.has(k)) {
        onChange(selected.filter((s) => campaignKey(s.provider, s.campaignId) !== k));
        return;
      }
      if (selected.length >= maxKeys) return;
      onChange([...selected, { provider: option.provider, campaignId: option.id }]);
    },
    [maxKeys, onChange, selected, selectedSet],
  );

  const label = useMemo(() => {
    if (selected.length === 0) return 'Choose campaigns…';
    const names = selected
      .map((s) => byKey.get(campaignKey(s.provider, s.campaignId))?.name ?? s.campaignId)
      .slice(0, 2);
    const extra = selected.length - names.length;
    return extra > 0 ? `${names.join(', ')} +${extra}` : names.join(', ');
  }, [byKey, selected]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(TRIGGER_CLASS, selected.length === 0 && 'text-muted-foreground')}
          aria-label="Campaigns for this value"
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal container={portalSurface ?? undefined}>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-[250] w-[min(100vw-2rem,560px)] rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or id…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-2">
            <CampaignPickerBody
              isLoading={isLoading}
              gated={gated}
              visible={visible}
              selectedSet={selectedSet}
              onToggle={toggle}
            />
          </div>
          {!gated && totalMatches > MAX_RENDERED_MATCHES ? (
            <p className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
              Showing {MAX_RENDERED_MATCHES} of {totalMatches} — refine your search.
            </p>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const CampaignPickerBody: FC<{
  isLoading: boolean;
  gated: boolean;
  visible: CampaignOption[];
  selectedSet: Set<string>;
  onToggle: (c: CampaignOption) => void;
}> = ({ isLoading, gated, visible, selectedSet, onToggle }) => {
  if (isLoading) {
    return <p className="py-3 text-center text-xs text-muted-foreground">Loading campaigns…</p>;
  }
  return (
    <>
      {gated ? (
        <p className="py-3 text-center text-xs text-muted-foreground">
          Type at least {MIN_SEARCH_CHARS} characters to search.
        </p>
      ) : null}
      {!gated && visible.length === 0 ? (
        <p className="py-3 text-center text-xs text-muted-foreground">No matches</p>
      ) : null}
      {visible.length > 0 ? (
        <ul className="space-y-0.5">
          {visible.map((c) => {
            const k = campaignKey(c.provider, c.id);
            return (
              <li key={k}>
                <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground">
                  <Checkbox
                    className="mt-0.5 shrink-0"
                    checked={selectedSet.has(k)}
                    onCheckedChange={() => onToggle(c)}
                    aria-label={`${c.name} (${c.provider})`}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate" title={c.name}>
                      {c.name}
                    </span>
                    {/* The search matches ids too, so show the id or that is unverifiable. */}
                    <span
                      className="truncate font-mono text-[11px] text-muted-foreground"
                      title={c.id}
                    >
                      {c.id}
                      {c.countryName ? ` · ${c.countryName}` : ''}
                    </span>
                  </span>
                  <ProviderBadge provider={trackerFromEnum(c.provider)} noun="campaign" />
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
    </>
  );
};
